import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useConfirm } from '../ui/confirm';
import UserManagement from './UserManagement';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import {
  SettingsSection,
  SettingsRow,
  SettingsHeader,
  DangerSection,
  DangerRow
} from './SettingsUI';

const UserSettings = () => {
  const { user, changePassword, logout, isAdmin } = useAuth();
  const confirm = useConfirm();
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({ ...prev, [name]: value }));
    if (passwordError) setPasswordError('');
    if (passwordSuccess) setPasswordSuccess('');
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setPasswordError('Please fill in all password fields');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('New password and confirmation do not match');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters long');
      return;
    }

    if (passwordForm.currentPassword === passwordForm.newPassword) {
      setPasswordError('New password must be different from current password');
      return;
    }

    try {
      setIsChangingPassword(true);

      const result = await changePassword(
        passwordForm.currentPassword,
        passwordForm.newPassword,
        passwordForm.confirmPassword
      );

      if (result.success) {
        setPasswordSuccess('Password changed successfully!');
        setPasswordForm({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setTimeout(() => setShowPasswordForm(false), 1500);
      } else {
        setPasswordError(result.error || 'Failed to change password');
      }
    } catch (error) {
      setPasswordError('Network error. Please try again.');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleLogout = async () => {
    const ok = await confirm({
      title: 'Sign Out',
      description: 'Are you sure you want to sign out?',
      confirmText: 'Sign Out',
    });
    if (ok) {
      await logout();
    }
  };

  return (
    <div className="py-4 space-y-6">
      <SettingsHeader title="User Account" />

      <SettingsSection title="Account">
        <SettingsRow label="Username" value={user?.username} />
        <SettingsRow 
          label="Role" 
          value={
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 uppercase">
              {user?.role}
            </span>
          } 
        />
        <SettingsRow 
          label="Last Login" 
          value={user?.lastLogin ? new Date(user.lastLogin).toLocaleString() : 'Never'} 
        />
        <SettingsRow 
          label="Account Created" 
          value={user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'Unknown'} 
        />
      </SettingsSection>

      <SettingsSection title="Security">
        <SettingsRow
          label="Change Password"
          description="Update your account password"
          chevron
          onClick={() => setShowPasswordForm(!showPasswordForm)}
        />
      </SettingsSection>

      {showPasswordForm && (
        <div className="px-4">
          <form onSubmit={handlePasswordSubmit} className="p-4 bg-card rounded-xl border border-border/60 space-y-4">
            {passwordError && (
              <Alert variant="destructive">
                <AlertDescription>{passwordError}</AlertDescription>
              </Alert>
            )}

            {passwordSuccess && (
              <Alert>
                <AlertDescription>{passwordSuccess}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="currentPassword">Current Password</Label>
              <Input
                type="password"
                id="currentPassword"
                name="currentPassword"
                value={passwordForm.currentPassword}
                onChange={handlePasswordChange}
                disabled={isChangingPassword}
                autoComplete="current-password"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="newPassword">New Password</Label>
              <Input
                type="password"
                id="newPassword"
                name="newPassword"
                value={passwordForm.newPassword}
                onChange={handlePasswordChange}
                disabled={isChangingPassword}
                autoComplete="new-password"
                minLength="6"
                required
              />
              <p className="text-xs text-muted-foreground">At least 6 characters</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <Input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={passwordForm.confirmPassword}
                onChange={handlePasswordChange}
                disabled={isChangingPassword}
                autoComplete="new-password"
                required
              />
            </div>

            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowPasswordForm(false)}
                disabled={isChangingPassword}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isChangingPassword} className="flex-1">
                {isChangingPassword ? 'Changing...' : 'Change Password'}
              </Button>
            </div>
          </form>
        </div>
      )}

      {isAdmin() && <UserManagement />}

      <DangerSection>
        <DangerRow
          label="Sign Out"
          description="End your current session"
          buttonLabel="Sign Out"
          onClick={handleLogout}
        />
      </DangerSection>
    </div>
  );
};

export default UserSettings;
